import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Alert, Box, CircularProgress, Snackbar, Stack, Typography, styled } from '@mui/material'

import { IBankInstitution } from '@/types'
import { Search } from '@/components/UI/Search'
import { DataTable, Order } from '@/components/UI/DataTable'
import { ErrorPage } from '@/pages/Error'
import { usePermissions } from '@/domain/permissions/usePermissions'
import SearchLogo from '@/assets/search.svg'
import { DEFAULT_SORT_BY, institutionColumns } from './Columns'
import { useInstitutions } from './useInstitutions'
import { apiErrorMessage } from '@/utils/apiError'
import { useUpdateDisplayOrder } from './useUpdateDisplayOrder'
import { DisplayOrderEditing } from './DisplayOrderCell'
import { ConfirmDisplayOrderDialog } from './ConfirmDisplayOrderDialog'

const isMissing = (value: unknown) => value === null || value === undefined || value === ''

// A display order of 0 means the institution has not been placed, so it sorts after every ranked
// one in both directions, followed only by those the service sent no order for at all.
const unrankedTier = (value: unknown) => (isMissing(value) ? 2 : value === 0 ? 1 : 0)

// Numbers compare numerically; everything else as text, case-insensitively and
// with embedded digits handled naturally ("Bank 2" before "Bank 10").
const compare = (a: unknown, b: unknown): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export const Institutions: React.FC = () => {
  const [search, setSearch] = useState('')
  // Display order is the rank the service assigns for presentation, so it is
  // what an agent expects to see before touching anything.
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState<Order>('asc')
  const navigate = useNavigate()

  const { data: institutions, isLoading, isError } = useInstitutions()

  // Changing the order customers see banks in is an Admin action; everyone else sees it read-only.
  const { user: operator } = useAuth0()
  const { isAdmin } = usePermissions(operator?.['https://carringtonlabs.io/appRole'])
  const canEditDisplayOrder = isAdmin()

  // One institution at a time: the row being edited with what has been typed so far, and the
  // change awaiting confirmation.
  const [edit, setEdit] = useState<{ id: string; draft: string } | null>(null)
  const [pending, setPending] = useState<{ institution: IBankInstitution; displayOrder: number }>()
  const [saveError, setSaveError] = useState<string>()
  const [notice, setNotice] = useState('')
  const updateDisplayOrder = useUpdateDisplayOrder()

  const stopEditing = () => {
    setEdit(null)
    setPending(undefined)
    setSaveError(undefined)
  }

  // Backing out of the dialog returns to the editor with the typed value, rather than dropping it.
  const cancelConfirmation = () => {
    setPending(undefined)
    setSaveError(undefined)
  }

  const confirmSave = () => {
    if (!pending) return
    const { institution, displayOrder } = pending
    setSaveError(undefined)
    updateDisplayOrder.mutate(
      { institutionId: institution.id, displayOrder },
      {
        onSuccess: () => {
          stopEditing()
          setNotice(`${institution.name} display order set to ${displayOrder}.`)
        },
        onError: error => setSaveError(apiErrorMessage(error)),
      }
    )
  }

  // The supported-bank list is bounded and fetched whole, so filtering client
  // side avoids a round trip per keystroke. Move this server side if the
  // endpoint ever paginates.
  const filteredInstitutions = useMemo(() => {
    if (!institutions) return []
    const term = search.trim().toLowerCase()
    if (!term) return institutions
    return institutions.filter(institution =>
      [institution.name, institution.id].some(field =>
        String(field ?? '')
          .toLowerCase()
          .includes(term)
      )
    )
  }, [institutions, search])

  // Sorting is client side because the whole list is already in memory; the
  // DataTable only reports the requested column and direction.
  const sortedInstitutions = useMemo(() => {
    return [...filteredInstitutions].sort((rowA, rowB) => {
      const a = rowA[sortBy]
      const b = rowB[sortBy]

      if (sortBy === 'displayOrder') {
        const tier = unrankedTier(a) - unrankedTier(b)
        if (tier !== 0) return tier
        // Unranked rows are listed alphabetically whatever the direction.
        if (unrankedTier(a) > 0) return compare(rowA.name, rowB.name)
      }

      // Rows the service gave no value for sink to the bottom in both
      // directions, rather than flooding the top when reversed.
      if (isMissing(a) || isMissing(b)) {
        if (isMissing(a) && isMissing(b)) return 0
        return isMissing(a) ? 1 : -1
      }

      const result = compare(a, b)
      if (result !== 0) return sortOrder === 'asc' ? result : -result

      // Ties resolve by name so the order is stable and predictable.
      return sortBy === 'name' ? 0 : compare(rowA.name, rowB.name)
    })
  }, [filteredInstitutions, sortBy, sortOrder])

  // An edit whose row a search has hidden is set aside rather than blocking every visible row: the
  // other edit buttons come back, and clearing the search brings the editor back with its draft.
  // Starting another edit replaces it, as nothing of it has been sent.
  const editingVisible = !!edit && filteredInstitutions.some(item => item.id === edit.id)

  const editing: DisplayOrderEditing = {
    canEdit: canEditDisplayOrder,
    editingId: editingVisible ? edit.id : null,
    draft: edit?.draft ?? '',
    onDraftChange: draft => setEdit(current => (current ? { ...current, draft } : current)),
    isConfirming: !!pending,
    onStart: institution =>
      setEdit({
        id: institution.id,
        draft: typeof institution.displayOrder === 'number' ? String(institution.displayOrder) : '',
      }),
    onCancel: stopEditing,
    onSave: (institution, displayOrder) => setPending({ institution, displayOrder }),
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Institutions'}
        message={
          "We couldn't retrieve the supported bank list at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        }
      />
    )
  }

  if (!institutions || institutions.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Institutions Found</Title>
        <Message>There are no supported bank institutions to show.</Message>
      </EmptyContainer>
    )
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        pb={2}
      >
        <Typography variant="h6">Bank Institutions</Typography>
        <Search
          searchValue={search}
          onSearch={setSearch}
          placeholder="Search by Institution Name or ID"
        />
      </Stack>

      {sortedInstitutions.length === 0 ? (
        <EmptyContainer>
          <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
          <Title>No Institutions Found</Title>
          <Message>No institution matches "{search}".</Message>
        </EmptyContainer>
      ) : (
        <DataTable<IBankInstitution>
          rows={sortedInstitutions}
          columns={institutionColumns(editing)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(property, order) => {
            setSortBy(String(property))
            setSortOrder(order)
          }}
          handleRowClick={institution => navigate(`/institutions/${institution.id}`)}
        />
      )}

      {pending && (
        <ConfirmDisplayOrderDialog
          open
          institution={pending.institution}
          displayOrder={pending.displayOrder}
          isSaving={updateDisplayOrder.isPending}
          error={saveError}
          onCancel={cancelConfirmation}
          onConfirm={confirmSave}
        />
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setNotice('')} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
}))
